import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Upload, ArrowLeft, FileText, Loader2, Flame, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [partner, setPartner] = useState('place_des_energies');
  const [energyType, setEnergyType] = useState('gas');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
    } else {
      toast.error('Veuillez sélectionner un fichier PDF');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'application/pdf') {
        setFile(droppedFile);
      } else {
        toast.error('Veuillez sélectionner un fichier PDF');
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('partner', partner);
      formData.append('energy_type', energyType);

      const response = await axios.post(`${API}/offers/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 90000, // 90 secondes pour l'extraction IA
      });

      toast.success('Offre créée avec succès!');
      navigate(`/edit/${response.data.id}`);
    } catch (error) {
      console.error('Upload error:', error);
      
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Le temps d\'extraction a expiré. Veuillez réessayer.');
      } else {
        toast.error('Erreur lors du téléchargement du PDF. Veuillez vérifier le format du fichier.');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 rounded-sm"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour au tableau de bord
          </Button>
          <h1 className="text-4xl font-heading tracking-tight">Télécharger une offre</h1>
          <p className="text-muted-foreground mt-2">
            Importez votre PDF d'offre énergétique pour le digitaliser
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <Card className="border border-border rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading">Import PDF</CardTitle>
            <CardDescription>
              Sélectionnez ou déposez votre fichier PDF d'offre
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Sélecteur de type d'énergie */}
            <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-orange-50 rounded-lg border">
              <label className="block text-sm font-semibold mb-3">
                Type d'énergie :
              </label>
              <div className="flex gap-4">
                <label 
                  className={`flex items-center cursor-pointer px-4 py-3 rounded-lg border-2 transition-all ${
                    energyType === 'gas' 
                      ? 'border-orange-500 bg-orange-50 shadow-sm' 
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="energyType"
                    value="gas"
                    checked={energyType === 'gas'}
                    onChange={(e) => setEnergyType(e.target.value)}
                    className="sr-only"
                  />
                  <Flame className={`h-5 w-5 mr-2 ${energyType === 'gas' ? 'text-orange-500' : 'text-gray-400'}`} />
                  <span className={`font-medium ${energyType === 'gas' ? 'text-orange-700' : 'text-gray-600'}`}>
                    Gaz naturel
                  </span>
                </label>
                <label 
                  className={`flex items-center cursor-pointer px-4 py-3 rounded-lg border-2 transition-all ${
                    energyType === 'electricity' 
                      ? 'border-blue-500 bg-blue-50 shadow-sm' 
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="energyType"
                    value="electricity"
                    checked={energyType === 'electricity'}
                    onChange={(e) => setEnergyType(e.target.value)}
                    className="sr-only"
                  />
                  <Zap className={`h-5 w-5 mr-2 ${energyType === 'electricity' ? 'text-blue-500' : 'text-gray-400'}`} />
                  <span className={`font-medium ${energyType === 'electricity' ? 'text-blue-700' : 'text-gray-600'}`}>
                    Électricité
                  </span>
                </label>
              </div>
            </div>

            {/* Sélecteur de partenaire */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium mb-3">
                Sélectionnez le partenaire :
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="partner"
                    value="place_des_energies"
                    checked={partner === 'place_des_energies'}
                    onChange={(e) => setPartner(e.target.value)}
                    className="mr-2 h-4 w-4"
                  />
                  <span className="text-sm">Place des Énergies</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="partner"
                    value="cousin_hub"
                    checked={partner === 'cousin_hub'}
                    onChange={(e) => setPartner(e.target.value)}
                    className="mr-2 h-4 w-4"
                  />
                  <span className="text-sm">Cousin Hub</span>
                </label>
              </div>
            </div>
            <div
              className={`border-2 border-dashed rounded-sm p-12 text-center transition-colors ${
                dragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="upload-dropzone"
            >
              {file ? (
                <div className="space-y-4">
                  <FileText className="h-16 w-16 text-primary mx-auto" />
                  <div>
                    <p className="text-lg font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={() => setFile(null)}
                      variant="outline"
                      className="rounded-sm"
                      data-testid="remove-file-btn"
                    >
                      Supprimer
                    </Button>
                    <Button
                      onClick={handleUpload}
                      disabled={uploading}
                      className="rounded-sm"
                      data-testid="upload-submit-btn"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Extraction en cours...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Télécharger et extraire
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="h-16 w-16 text-muted-foreground mx-auto" />
                  <div>
                    <p className="text-lg mb-2">Déposez votre fichier PDF ici</p>
                    <p className="text-sm text-muted-foreground mb-4">ou</p>
                    <label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileChange}
                        className="hidden"
                        data-testid="file-input"
                      />
                      <Button variant="outline" className="rounded-sm" asChild>
                        <span>Parcourir les fichiers</span>
                      </Button>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">Format accepté : PDF (max 10MB)</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Info Section */}
        <div className="mt-8 p-6 bg-muted/30 border border-border rounded-sm">
          <h3 className="font-heading text-lg mb-3">Comment ça fonctionne ?</h3>
          <ol className="space-y-2 text-sm text-muted-foreground">
            <li>1. Téléchargez votre PDF d'offre énergétique</li>
            <li>2. Notre IA extrait automatiquement toutes les données</li>
            <li>3. Vérifiez et modifiez les informations si nécessaire</li>
            <li>4. Partagez l'offre digitale via un lien unique</li>
          </ol>
        </div>
      </main>
    </div>
  );
};

export default UploadPage;
